import { Feature, Point, Polygon, MultiPolygon, Position } from "geojson";
import type { Job } from "../../../models";
import { POLYGON_COLORS } from "./JobMap.types";

/**
 * Get a random color from the polygon colors palette
 */
export function getRandomPolygonColor(): string {
	const index = Math.floor(Math.random() * POLYGON_COLORS.length);
	return POLYGON_COLORS[index];
}

/**
 * Get a consistent color for a job based on its ID.
 * Uses a simple hash function to map the job ID to a stable color index.
 */
export function getJobColor(jobId: string): string {
	let hash = 0;
	for (let i = 0; i < jobId.length; i++) {
		const char = jobId.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32-bit integer
	}
	const index = Math.abs(hash) % POLYGON_COLORS.length;
	return POLYGON_COLORS[index];
}

/**
 * Safely extract a property from a GeoJSON feature
 */
export function getShapeFeatureProperty(feature: Feature<Polygon | Point>, key: string): string {
	return feature.properties && key in feature.properties ? String(feature.properties[key]) : "";
}

/**
 * Parse the change value from feature properties
 */
export function parseChangeInfo(change: string): {
	hasChange: boolean;
	changeType: string;
	changeReason: string;
} {
	const [changeType, changeReason] = change.includes(":") ? change.split(":") : ["", ""];
	const hasChange = Boolean(changeType && changeType !== "None");
	return { hasChange, changeType, changeReason: changeReason || "" };
}

export function getJobBoundaryGeometry(job: Job): Polygon | MultiPolygon | null {
	const boundaryFromFiles = job.files?.boundaryGeoJSON;
	if (boundaryFromFiles?.features?.[0]?.geometry) {
		const geom = boundaryFromFiles.features[0].geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			return geom as Polygon | MultiPolygon;
		}
	}

	return null;
}

/**
 * Generate parallel hatch lines clipped to a polygon or multipolygon.
 * Returns an array of line segments as Position[][] (GeoJSON [lng, lat] coords).
 */
export async function generateHatchLines(
	geometry: Polygon | MultiPolygon,
	angleDegrees: number,
	spacingMeters: number,
): Promise<Position[][]> {
	const [
		{ feature: turfFeature, point: turfPoint, lineString },
		{ bbox },
		{ center },
		{ distance },
		{ destination },
		{ getCoord },
		{ lineSplit },
		{ midpoint },
		{ booleanPointInPolygon },
	] = await Promise.all([
		import("@turf/helpers"),
		import("@turf/bbox"),
		import("@turf/center"),
		import("@turf/distance"),
		import("@turf/destination"),
		import("@turf/invariant"),
		import("@turf/line-split"),
		import("@turf/midpoint"),
		import("@turf/boolean-point-in-polygon"),
	]);

	const feat = turfFeature(geometry);
	const bb = bbox(feat);
	const bboxCenter = center(feat);
	const diagonal = distance(turfPoint([bb[0], bb[1]]), turfPoint([bb[2], bb[3]]), {
		units: "meters",
	});

	if (diagonal < spacingMeters) return [];

	const halfDiag = diagonal / 2;
	const results: Position[][] = [];

	for (let offset = -halfDiag; offset <= halfDiag; offset += spacingMeters) {
		const perpAngle = angleDegrees + 90;
		const offsetPoint = destination(bboxCenter, offset / 1000, perpAngle, { units: "kilometers" });

		const lineStart = destination(offsetPoint, halfDiag / 1000, angleDegrees, { units: "kilometers" });
		const lineEnd = destination(offsetPoint, halfDiag / 1000, angleDegrees + 180, { units: "kilometers" });

		const line = lineString([getCoord(lineStart), getCoord(lineEnd)]);

		try {
			const split = lineSplit(line, feat);

			for (const segment of split.features) {
				const coords = segment.geometry.coordinates;
				const mid = midpoint(turfPoint(coords[0]), turfPoint(coords[coords.length - 1]));
				if (booleanPointInPolygon(mid, feat)) {
					results.push(coords);
				}
			}
		} catch {
			continue;
		}
	}

	return results;
}
